import { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useLocation, useHistory } from 'react-router-dom'
import { Box,Button, Grid, Typography } from '@aura/components'
import { withStyles } from '@aura/components/styles'
import { useAuraThemeSwitcher } from '@aura/components/themes'
import { useTranslation } from 'react-i18next'
import { useConfig } from '../../../../utils/config'
import AuthStatus from '../../../../constants/auth-status'
import AppBarNew from '../../../../components/app-bar-tool'
import FooterNew from '../../../../components/footer-new'
import CustomLoader from '../../../../components/custom-loader'
import MFA from '../../../login/components/mfa-main'
import SessionTimeout from '../../../../components/session-timeout'
import SUPPORTED_THEMES from '../../../../constants/theme'
import LogoutStatus from '../../../../constants/logout-status'
import { clearCookies } from '../../../../utils/cookie'
import { cookieNameAppAutoRedirect } from '../../../../constants/app'
import ErrorPage from './error-page'
import styles from './styles'


const INVALID_TOKEN_ILLUSTRATIONS = {
    [SUPPORTED_THEMES.DARK]: '/img/linkexp_dark.svg',
    [SUPPORTED_THEMES.LIGHT]: '/img/linkexp.svg'
}
const GENERIC_ERROR_ILLUSTRATIONS = {
    [SUPPORTED_THEMES.DARK]: '/img/someThingWentWrong-dark.png',
    [SUPPORTED_THEMES.LIGHT]: '/img/somethingWentWrong-light.png'
}
const Component = ({
    authData,
    classes,
    isProcessing,
    onVerifyLoginMagicLink,
    magicLinkVerificationStatus,
    authVerified,
    authenticatorData,
    authenticatorStatus,
    authStatus,
    dataQRCode,
    error,
    errorSession,
    initAuthenticators,
    initEnrollAuthFactor,
    initPush,
    logoutOktaSession,
    onActivateAuthAuthenticatorCode,
    onActivateAuthenticatorCode,
    onBackToAuthenticators,
    onBackToOptionalAuthenticators,
    onBackFallbackEnforceMFA,
    onBackFallbackEnforceMFATwoSteps,
    onEnroll,
    onFinish,
    onQRCodeCannotScan,
    onQRCodeCannotScanNext,
    onQRCodeNext,
    onResendAuthChallenge,
    onResendChallenge,
    onSendChallenge,
    onSetAuthenticator,
    onSetDeviceType,
    onSetOptionalAuthenticator,
    onSkipEnrollment,
    onVerifyAccountRecovery,
    onVerifyAuthAccountRecovery,
    onVerifyAuthenticatorCode,
    qrCodeStatus,
    resetAuthVerified,
    resendSuccess,
    resendSuccessMsg,
    stepNumber,
    isVerified,
    mfaRequired,
    getAccountRecovery,
    accountRecoveryData,
    accountRecoveryStatus,
    onEnrollInit,
    validatePhoneAtLogin,
    OnValidatePhoneAtLogin,
    sessionTimeout,
    errorMagicLinkVerify,
    terminateOAGSession,
    logoutStatusOkta,
    logoutStatusOAG
}) => {
    const config = useConfig()
    const { theme, updateTheme } = useAuraThemeSwitcher()
    const history = useHistory()
    const location = useLocation()
    const [ showInvalidToken, setShowInvalidToken ] = useState(false)
    const params = new URLSearchParams(location.search)
    const { t: translate, i18n } = useTranslation()
    const passwordLessToken = params.get('passwordLessToken')
    const language = params.get('language')
    const themeFromParams = params.get('theme')
    const  [ errorPageIcon, setErrorPageIcon ] = useState(INVALID_TOKEN_ILLUSTRATIONS)
    const [ errorPageTitle, setErrorPageTitle ] = useState('Password less token is invalid or expired.')
    const [ errorPageDescription, setErrorPageDescription ] = useState('magicLink.linkExpired')
    const handleReturnToLogin = useCallback(() => {
        history.push(`/login/${language}`)
    }, [ history, language ])
    
    const icons = {
        'dark': '/img/magiclink-dark.svg?url',
        'light': '/img/magiclink-light.svg?url'
    }
    const [ loading, setLoading ] = useState(true)
    const [ showContinuePage, setShowContinuePage ] = useState(true)
    const handleContinue = useCallback((e, text) => {
        setShowContinuePage(false)
        onVerifyLoginMagicLink({ passwordLessToken })
    }, [])

    const mainDomain = window.location.hostname.substring(window.location.hostname.indexOf('.'))

    useEffect(() => {
        if (magicLinkVerificationStatus === AuthStatus.SUCCESS && authData) {
            if(authData.appGAURL){
                document.cookie = `${cookieNameAppAutoRedirect}=true;domain=${mainDomain};path=/;SameSite=Strict`
                window.location.replace(authData.appGAURL)
            }else{
                setShowContinuePage(false)
            }
        }
        if (magicLinkVerificationStatus === AuthStatus.ERROR) {
            const INVALID_TOKEN = 'Password less token is invalid or expired.'
            if((Array.isArray(errorMagicLinkVerify) && errorMagicLinkVerify[0]?.message !== INVALID_TOKEN) || (typeof errorMagicLinkVerify === 'string' && errorMagicLinkVerify !== INVALID_TOKEN)){
                setErrorPageIcon(GENERIC_ERROR_ILLUSTRATIONS)
                setErrorPageTitle('somethingWentWrong')
                setErrorPageDescription('somethingWentWrong')
            }
            setShowInvalidToken(true)
        }
    }, [ authData, magicLinkVerificationStatus, language, history ])

    const handleLogout = useCallback(() => {
        if (config?.logoutOAGSession) {
            terminateOAGSession(config?.logoutOAGSession)
        }
    }, [])

    useEffect(() => {
        if (config?.oktaCurrentSession) {
            logoutOktaSession(config?.oktaCurrentSession)
        }
    }, [])

    useEffect(() => {
        if (logoutStatusOkta === LogoutStatus.SUCCESS || logoutStatusOkta === LogoutStatus.ERROR) {
            handleLogout()
        }
    }, [ logoutStatusOkta ])

    useEffect(() => {
        if (logoutStatusOAG === LogoutStatus.SUCCESS || logoutStatusOAG === LogoutStatus.ERROR) {
            clearCookies(config?.cmxClean, 'OAG cookies')
            clearCookies(config?.secure2Clean, 'OAG secure2 cookies')
            clearCookies(config?.protectedClean, 'OAG protected cookies')
        }
    },[ logoutStatusOAG ])
    
    useEffect(() => {
        i18n.changeLanguage(language)
        if (themeFromParams && theme.palette.mode !== themeFromParams) {
            updateTheme({ palette: themeFromParams })
        }
        setLoading(false)
    }, [ magicLinkVerificationStatus ])

    if (sessionTimeout) {
        return (
            <div className={classes.sessionTimeoutWrapper}>
                <SessionTimeout needToShowHelpText={false} customVerbiage = {'sessionTimeoutMagicLink'} />
            </div>
        )
    }

    if (magicLinkVerificationStatus === AuthStatus.PENDING || loading || isProcessing || (!showContinuePage && (magicLinkVerificationStatus  === AuthStatus.SUCCESS && authData && authData.appGAURL))) {
        return <CustomLoader 
            label={translate('loading')}
            style={{ background: theme.palette.mode === 'dark' ? '#101935' : '#FFFFFF', height: '100vh', width: 'auto' }}
        />
    }

    return (
        <>
            <AppBarNew />
            {/* Continue page when user land from the mail by clicking the button */}
            {showContinuePage && !authData && <Grid container className={classes.centralLoginBox}>
                <Box component='img' src={icons[theme.palette.mode]} />
                <Grid className={classes.magicLinkContent}>
                    {/* Magic Link Content */}
                    <Typography className={classes.heading}>{translate('magicLink.title')}</Typography>
                    <p className={classes.supportText}>{translate('magicLink.supportText')}</p>
                    <Button
                        key={'continue-button'}
                        className={classes.button}
                        onClick={()=>handleContinue()}
                    >
                        {translate('magicLink.continueButton')}
                    </Button>
                </Grid>                
            </Grid>}
            {/* If we are not on the continue page and magic link is verified and we don't have appGAURL, show MFA */}
            { !showContinuePage && magicLinkVerificationStatus  === AuthStatus.SUCCESS && authData && !authData.appGAURL
            && <MFA {...{
                accountRecoveryData,
                accountRecoveryStatus,
                appGAURL: authData ? authData.appGAURL : '',
                authData,
                authenticatorData,
                authenticatorStatus,
                authVerified,
                dataQRCode,
                error,
                errorSession,
                getAccountRecovery,
                initAuthenticators,
                initEnrollAuthFactor,
                initPush,
                isProcessing: authStatus === AuthStatus.PENDING,
                isVerified,
                mfaRequired,
                onActivateAuthAuthenticatorCode,
                onActivateAuthenticatorCode,
                onBackFallbackEnforceMFA,
                onBackFallbackEnforceMFATwoSteps,
                onBackToAuthenticators,
                onBackToOptionalAuthenticators,
                onEnroll,
                onEnrollInit,
                onFinish,
                onQRCodeCannotScan,
                onQRCodeCannotScanNext,
                onQRCodeNext,
                onResendAuthChallenge,
                onResendChallenge,
                onSendChallenge,
                onSetAuthenticator,
                onSetDeviceType,
                onSetOptionalAuthenticator,
                onSkipEnrollment,
                OnValidatePhoneAtLogin,
                onVerifyAccountRecovery,
                onVerifyAuthAccountRecovery,
                onVerifyAuthenticatorCode,
                qrCodeStatus,
                resendSuccess,
                resendSuccessMsg,
                resetAuthVerified,
                stepNumber,
                validatePhoneAtLogin
            }} /> 
            }  
            { showInvalidToken && <ErrorPage imgObj={errorPageIcon} onResend={handleReturnToLogin} errorMessage={errorPageTitle} errorTitle={translate(errorPageDescription)} />}
            { (showInvalidToken || (showContinuePage && !authData)) && <FooterNew /> }
        </>
    )
}

Component.displayName = 'MagicLink'

Component.propTypes = {
    classes: PropTypes.object,
    isProcessing: PropTypes.bool,
    onVerifyLoginMagicLink: PropTypes.func.isRequired,
}

export default withStyles(Component, styles)